import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Linkedin, Mail, Phone, Heart, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import WCLCLogo from '../ui/WCLCLogo';
import { siteConfig } from '../../data/mock';
import { toast } from 'sonner';
import api, { formatApiErrorDetail } from '../../lib/api';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    try {
      const { data } = await api.post('/newsletter', { email });
      toast.success(data.message);
      setEmail('');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const quickLinks = [
    { name: 'About Us', path: '/about' },
    { name: 'Programs', path: '/programs' },
    { name: 'Events', path: '/events' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Membership', path: '/membership' },
    { name: 'Contact', path: '/contact' },
  ];

  const getInvolved = [
    { name: 'Founding Members', path: '/founding-members' },
    { name: 'Join Us', path: '/membership' },
    { name: 'Volunteer', path: '/get-involved#volunteer' },
    { name: 'Donate', path: '/donate' },
    { name: 'Partner With Us', path: '/get-involved#partner' },
    { name: 'About GFWC', path: '/gfwc' },
  ];

  return (
    <footer className="bg-[#e7cfdf] text-[#3c4a2a]">
      {/* Newsletter Section - Olive Green */}
      <div className="bg-[#5d6b3c] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-serif text-2xl text-white mb-2">
                Stay Connected
              </h3>
              <p className="text-white/90">
                Join our newsletter for updates on events, programs, and community impact.
              </p>
            </div>
            <form onSubmit={handleNewsletterSubmit} className="flex w-full md:w-auto gap-3">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full md:w-72 bg-white/15 border-white/30 text-white placeholder:text-white/60 focus:border-white focus:bg-white/20"
                required
              />
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-[#f1e3eb] hover:bg-white text-[#3c4a2a] px-6 whitespace-nowrap"
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer Content - Shell Pink */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Brand Column */}
            <div className="lg:col-span-1">
              <Link to="/" className="inline-block mb-6">
                <WCLCLogo size="large" />
              </Link>
              <p className="text-[#3c4a2a]/70 text-sm mb-6 leading-relaxed">
                A nonpartisan organization dedicated to cultivating community advancement through women's leadership.
              </p>
              <div className="flex space-x-4">
                <a
                  href={siteConfig.socialLinks.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-[#bd8cae]/30 text-[#3c4a2a] flex items-center justify-center hover:bg-[#5d6b3c] hover:text-white transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a
                  href={siteConfig.socialLinks.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-[#bd8cae]/30 text-[#3c4a2a] flex items-center justify-center hover:bg-[#5d6b3c] hover:text-white transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a
                  href={siteConfig.socialLinks.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-[#bd8cae]/30 text-[#3c4a2a] flex items-center justify-center hover:bg-[#5d6b3c] hover:text-white transition-colors"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-serif text-lg text-[#3c4a2a] mb-6">Quick Links</h4>
              <ul className="space-y-3">
                {quickLinks.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.path}
                      className="text-[#3c4a2a]/70 hover:text-[#5d6b3c] transition-colors text-sm"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Get Involved */}
            <div>
              <h4 className="font-serif text-lg text-[#3c4a2a] mb-6">Get Involved</h4>
              <ul className="space-y-3">
                {getInvolved.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.path}
                      className="text-[#3c4a2a]/70 hover:text-[#5d6b3c] transition-colors text-sm"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="font-serif text-lg text-[#3c4a2a] mb-6">Contact Us</h4>
              <ul className="space-y-4">
                <li className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-[#5d6b3c] flex-shrink-0" />
                  <a
                    href={`tel:${siteConfig.phone}`}
                    className="text-[#3c4a2a]/70 hover:text-[#5d6b3c] transition-colors text-sm"
                  >
                    {siteConfig.phone}
                  </a>
                </li>
                <li className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-[#5d6b3c] flex-shrink-0" />
                  <a
                    href={`mailto:${siteConfig.email}`}
                    className="text-[#3c4a2a]/70 hover:text-[#5d6b3c] transition-colors text-sm"
                  >
                    {siteConfig.email}
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#3c4a2a]/10 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-[#3c4a2a]/60 text-sm">
              © {new Date().getFullYear()} {siteConfig.name}. All rights reserved.
            </p>
            <div className="flex items-center space-x-6">
              <Link
                to="/privacy"
                className="text-[#3c4a2a]/60 hover:text-[#5d6b3c] transition-colors text-sm"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-[#3c4a2a]/60 hover:text-[#5d6b3c] transition-colors text-sm"
              >
                Terms of Service
              </Link>
            </div>
            <p className="text-[#3c4a2a]/60 text-sm flex items-center">
              Made with <Heart className="w-4 h-4 text-[#d84f5b] mx-1" /> in Lincoln County
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
